import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // add useSearchParams
import {
  Grid, TextField, Button, Typography, Box, Paper, Avatar,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  CircularProgress, Checkbox, Alert,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../../hooks/useAuth';
import RegistrationStyles from './Registration.styles';

const NODE_SERVER =
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : 'https://ai-realtor-2ktt.onrender.com');

const Registration = () => {
  const [searchParams] = useSearchParams();
  const prefillEmail = searchParams.get('email') || '';
  const prefillPropertyId = searchParams.get('propertyId') || '';
  const isTenantInvite = Boolean(prefillEmail && prefillPropertyId);

  const [userType, setUserType] = useState('renter');
  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    email: prefillEmail,
    phone: '',
    password: '',
    confirmPassword: '',
    smsOptIn: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  const { register, ROLES } = useAuth();

  const handleUserTypeChange = (event) => setUserType(event.target.value);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhoneChange = (event) => {
    const digits = event.target.value.replace(/\D/g, '');
    const limitedDigits = digits.slice(0, 10);
    let formatted = '';
    if (limitedDigits.length > 0) formatted = limitedDigits.slice(0, 3);
    if (limitedDigits.length > 3) formatted += '-' + limitedDigits.slice(3, 6);
    if (limitedDigits.length > 6) formatted += '-' + limitedDigits.slice(6, 10);
    setFormData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    const { companyName, fullName, email, phone, password, confirmPassword, smsOptIn } = formData;

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }
    if (!smsOptIn) {
      setSubmitError('You must opt-in to SMS notifications to register');
      return;
    }

    try {
      setLoading(true);

      const needsCompany = !isTenantInvite && (userType === 'landlord' || userType === ROLES.MANAGER);
      const additionalData = needsCompany
        ? { companyName, fullName, phone, smsOptIn }
        : { fullName, phone, smsOptIn };

      const effectiveUserType = isTenantInvite ? 'renter' : userType;
      const userCredential = await register(email, password, effectiveUserType, additionalData);

      if (isTenantInvite) {
        const uid = userCredential?.user?.uid;

        try {
          const res = await fetch(`${NODE_SERVER}/api/auth/mark-email-verified`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid }),
          });
          if (!res.ok) {
            console.warn('[Registration] mark-email-verified failed, continuing anyway');
          }
        } catch (err) {
          console.warn('[Registration] mark-email-verified call error:', err.message);
        }

        navigate('/onboarding');
      } else {
        navigate('/email-verification-pending');
      }
    } catch (error) {
      console.error('Error registering user:', error);
      setSubmitError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showCompany = !isTenantInvite && (userType === 'landlord' || userType === ROLES.MANAGER);

  return (
    <Grid container sx={RegistrationStyles.container}>
      <Paper elevation={10} sx={RegistrationStyles.paper}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Avatar sx={RegistrationStyles.avatar}><LockOutlinedIcon /></Avatar>
          <Typography component="h1" variant="h5">Register</Typography>

          {isTenantInvite && (
            <Box sx={{
              mt: 2, px: 2, py: 1.5, width: '100%',
              bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 2,
            }}>
              <Typography variant="body2" fontWeight={600} color="info.main">
                You've been invited as a tenant
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create your account to receive your smart lock access code automatically.
              </Typography>
            </Box>
          )}

          <Box component="form" sx={RegistrationStyles.form} onSubmit={handleSubmit}>

            {!isTenantInvite && (
              <FormControl component="fieldset" sx={RegistrationStyles.radioGroup}>
                <FormLabel component="legend">I am a:</FormLabel>
                <RadioGroup row name="userType" value={userType} onChange={handleUserTypeChange}>
                  <FormControlLabel value="renter" control={<Radio />} label="Renter" />
                  <FormControlLabel value="landlord" control={<Radio />} label="Property Owner" />
                  <FormControlLabel value="manager" control={<Radio />} label="Property Manager" />
                </RadioGroup>
              </FormControl>
            )}

            {showCompany && (
              <TextField
                margin="normal" required fullWidth
                label="Company Name" name="companyName"
                autoComplete="organization" autoFocus
                sx={RegistrationStyles.textField} onChange={handleChange}
              />
            )}

            <TextField margin="normal" required fullWidth label="Full Name" name="fullName"
              autoComplete="name" autoFocus={!showCompany}
              sx={RegistrationStyles.textField} onChange={handleChange} />

            <TextField margin="normal" required fullWidth label="Email Address" name="email"
              autoComplete="email" sx={RegistrationStyles.textField}
              value={formData.email}
              onChange={isTenantInvite ? undefined : handleChange}
              disabled={isTenantInvite}
              helperText={isTenantInvite ? 'Set by your property manager — cannot be changed' : undefined}
            />

            <TextField margin="normal" required fullWidth label="Phone Number" name="phone"
              autoComplete="tel" sx={RegistrationStyles.textField} onChange={handlePhoneChange}
              value={formData.phone} placeholder="(555) 123-4567"
              helperText="Enter 10-digit phone number"
              inputProps={{ inputMode: 'tel', maxLength: 12 }} />

            <TextField margin="normal" required fullWidth name="password" label="Password"
              type="password" autoComplete="new-password"
              sx={RegistrationStyles.textField} onChange={handleChange} />

            <TextField margin="normal" required fullWidth name="confirmPassword" label="Confirm Password"
              type="password" autoComplete="new-password"
              sx={RegistrationStyles.textField} onChange={handleChange} />

            <FormControlLabel
              control={<Checkbox checked={formData.smsOptIn} onChange={handleChange} name="smsOptIn" color="primary" />}
              label={
                <Typography variant="body2">
                  I agree to receive SMS notifications for showing alerts and system updates.
                  Message and data rates may apply. Reply STOP to opt-out at any time.
                </Typography>
              }
              sx={{ mt: 2, mb: 1 }}
            />

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                {submitError}
              </Alert>
            )}

            <Button type="submit" fullWidth variant="contained"
              sx={RegistrationStyles.registerButton} disabled={loading || !formData.smsOptIn}>
              {loading ? <CircularProgress size={24} /> : 'Register'}
            </Button>

          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

export default Registration;